/**
 * Summary Card component for displaying financial metrics
 * Inspired by personal finance apps (Mint, YNAB, Personal Capital)
 */

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatVND } from '@/utils/currencyUtils'
import { GoldTypeBreakdown } from './GoldTypeBreakdown'

interface GoldTypeBreakdownData {
  name: string
  quantity: number
  percent: number
}

interface SummaryCardProps {
  title: string
  value: string | number
  subtitle?: string
  change?: {
    value: number
    percent?: number
    label?: string
  }
  icon?: React.ComponentType<any>
  variant?: 'default' | 'positive' | 'negative' | 'neutral'
  onClick?: () => void
  className?: string
  breakdown?: {
    data: GoldTypeBreakdownData[]
    totalQuantity: number
  } | undefined
}

export function SummaryCard({
  title,
  value,
  subtitle,
  change,
  icon: Icon,
  variant = 'default',
  onClick,
  className,
  breakdown,
}: SummaryCardProps) {
  const isPositive = variant === 'positive' || (change && change.value >= 0)
  const isNegative = variant === 'negative' || (change && change.value < 0)

  const variantStyles = {
    default: 'border-border',
    positive: 'border-green-500/30 bg-green-500/10 dark:border-green-400/40 dark:bg-green-500/20',
    negative: 'border-red-500/30 bg-red-500/10 dark:border-red-400/40 dark:bg-red-500/20',
    neutral: 'border-border bg-muted/30',
  }

  const valueColor = isPositive
    ? 'text-green-600 dark:text-green-400'
    : isNegative
      ? 'text-red-600 dark:text-red-400'
      : 'text-foreground'

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-all duration-200 min-w-0',
        onClick && 'cursor-pointer',
        variantStyles[variant],
        className
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <p className="text-sm font-medium text-muted-foreground truncate">{title}</p>
          {Icon && (
            <Icon className="h-5 w-5 text-muted-foreground shrink-0" stroke={1.5} aria-hidden="true" />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 min-w-0">
          <p className={cn(
            'text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-right break-words overflow-hidden',
            valueColor
          )}>
            {value}
          </p>
          {subtitle && !breakdown && (
            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 break-words">{subtitle}</p>
          )}
          {breakdown && (
            <div className="pt-2">
              <GoldTypeBreakdown
                data={breakdown.data}
                totalQuantity={breakdown.totalQuantity}
                maxItems={5}
              />
            </div>
          )}
          {change && (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <Badge
                variant="outline"
                className={cn(
                  'text-xs font-semibold shrink-0',
                  isPositive && 'bg-green-100 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40 dark:hover:bg-green-500/30',
                  isNegative && 'bg-red-100 text-red-700 border-red-300 hover:bg-red-100 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/40 dark:hover:bg-red-500/30'
                )}
              >
                {isPositive ? '↑' : '↓'} {formatVND(Math.abs(change.value))}
                {change.percent !== undefined && ` (${change.percent.toFixed(2)}%)`}
              </Badge>
              {change.label && (
                <span className="text-xs text-muted-foreground truncate">{change.label}</span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

